import React, { useState } from 'react';
import { 
  Bold, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  Trash2,
  Plus,
  Merge,
  Split,
  Image as ImageIcon,
  Type,
  Palette,
  Columns,
  Rows,
  Upload,
  Grid3X3,
  Square,
  Ban,
  ArrowUpFromLine,
  ArrowDownFromLine,
  ArrowLeftFromLine,
  ArrowRightFromLine,
  GripHorizontal,
  GripVertical,
  ChevronDown,
  X
} from 'lucide-react';
import { Button, buttonVariants } from '@/app/components/ui/button';
import { TitleBlockCell, TitleBlockCellStyles } from '@/app/types/titleblock';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { Input } from '@/app/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/app/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Label } from '@/app/components/ui/label';
import { BorderMode } from '@/app/utils/titleBlockOps';
import { cn } from '@/app/components/ui/utils';

const AVAILABLE_ATTRIBUTES = [
  { key: 'project.name', label: 'Projekt Name' },
  { key: 'project.client', label: 'Bauherr' },
  { key: 'project.number', label: 'Projekt Nr.' },
  { key: 'plan.content', label: 'Plan Inhalt' },
  { key: 'plan.number', label: 'Plan Nr.' },
  { key: 'scale', label: 'Maßstab' },
  { key: 'meta.date', label: 'Datum' },
  { key: 'meta.drawnBy', label: 'Gezeichnet von' },
  { key: 'meta.checkedBy', label: 'Geprüft von' },
  { key: 'system.0', label: 'Höhensystem' },
  { key: 'system.1', label: 'Koordinatensystem' },
  { key: 'system.2', label: 'Grundlagenplan' },
];

export type GridAction = 
  | { type: 'INSERT_ROW', after: boolean }
  | { type: 'DELETE_ROW' }
  | { type: 'INSERT_COL', after: boolean }
  | { type: 'DELETE_COL' }
  | { type: 'MERGE', direction: 'right' | 'down' }
  | { type: 'MERGE_SELECTION' }
  | { type: 'SPLIT' }
  | { type: 'SET_BORDERS', mode: BorderMode, width?: number };

interface TitleBlockToolbarProps {
  selectedCells: TitleBlockCell[];
  onUpdateStyle: (updates: Partial<TitleBlockCellStyles>) => void;
  onUpdateCell: (updates: Partial<TitleBlockCell>) => void;
  onGridAction: (action: GridAction) => void;
}

export function TitleBlockToolbar({ selectedCells, onUpdateStyle, onUpdateCell, onGridAction }: TitleBlockToolbarProps) {
  if (!selectedCells || selectedCells.length === 0) return null;

  // Use the first cell as reference for displaying current values
  const primaryCell = selectedCells[0];
  const styles = primaryCell.styles || {};
  
  const isMultiSelect = selectedCells.length > 1;
  
  // State for image dialog
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState('');
  
  // Border width state (shared for the dropdown)
  const [borderWidth, setBorderWidth] = useState(styles.borderBottom?.width || 0.25);

  // Sync temp URL when dialog opens
  React.useEffect(() => {
     if (imageDialogOpen && primaryCell.type === 'image') {
        setTempImageUrl(primaryCell.value?.startsWith('http') ? primaryCell.value : '');
     }
  }, [imageDialogOpen, primaryCell]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          onUpdateCell({ value: event.target.result as string });
          setImageDialogOpen(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const applyBorder = (mode: BorderMode) => {
     onGridAction({ type: 'SET_BORDERS', mode, width: borderWidth });
  };

  return (
    <div className="flex flex-col gap-2 pointer-events-auto">
      {/* Top Row: Content & Basic Style */}
      <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-xl border border-gray-200">
        
        {/* Content Type & Value */}
        <div className="flex items-center border-r pr-2 gap-2">
          <div className="flex gap-1">
             <Button
                variant={primaryCell.type === 'text' ? 'secondary' : 'ghost'}
                size="icon" className="h-8 w-8"
                onClick={() => onUpdateCell({ type: 'text' })}
                title="Text"
             >
                <Type className="w-4 h-4" />
             </Button>
             <Button
                variant={primaryCell.type === 'image' ? 'secondary' : 'ghost'}
                size="icon" className="h-8 w-8"
                onClick={() => onUpdateCell({ type: 'image' })}
                title="Bild"
             >
                <ImageIcon className="w-4 h-4" />
             </Button>
             <Button
                variant={primaryCell.type === 'attribute' ? 'secondary' : 'ghost'}
                size="icon" className="h-8 w-8"
                onClick={() => onUpdateCell({ type: 'attribute' })}
                title="Variable"
             >
                <span className="font-mono text-xs">{'{}'}</span>
             </Button>
          </div>
          
          {primaryCell.type === 'image' ? (
             <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
                <DialogTrigger className={cn(buttonVariants({ variant: "outline", size: "sm" }), "h-8 text-xs w-32 justify-start px-2 overflow-hidden text-ellipsis whitespace-nowrap")}>
                    {primaryCell.value ? 'Bild ändern...' : 'Bild wählen...'}
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Bild auswählen</DialogTitle>
                        <DialogDescription className="sr-only">
                            Laden Sie ein Bild hoch oder geben Sie eine URL ein.
                        </DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue="url" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="url">URL</TabsTrigger>
                            <TabsTrigger value="upload">Upload</TabsTrigger>
                        </TabsList>
                        <TabsContent value="url" className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="img-url">Bild URL</Label>
                                <Input 
                                    id="img-url" 
                                    placeholder="https://example.com/image.png" 
                                    value={tempImageUrl}
                                    onChange={(e) => setTempImageUrl(e.target.value)}
                                />
                            </div>
                            <Button onClick={() => { onUpdateCell({ value: tempImageUrl }); setImageDialogOpen(false); }} className="w-full">
                                Übernehmen
                            </Button>
                        </TabsContent>
                        <TabsContent value="upload" className="space-y-4 py-4">
                            <div className="flex items-center justify-center w-full">
                                <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 mb-3 text-gray-400" />
                                        <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Klicken zum Upload</span></p>
                                        <p className="text-xs text-gray-500">PNG, JPG oder SVG</p>
                                    </div>
                                    <input id="dropzone-file" type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                                </label>
                            </div>
                        </TabsContent>
                    </Tabs>
                </DialogContent>
             </Dialog>
          ) : (
             <div className="flex items-center gap-1">
                 <Input 
                    className="h-8 w-32 text-xs" 
                    placeholder="Inhalt..."
                    value={isMultiSelect ? (primaryCell.value ? `${primaryCell.value}...` : '...') : (primaryCell.value || '')}
                    onChange={(e) => {
                        const newVal = e.target.value;
                        const isAttr = newVal.startsWith('{{');
                        onUpdateCell({ 
                            value: newVal,
                            type: isAttr ? 'attribute' : 'text'
                        });
                    }}
                 />
                 <Popover>
                    <PopoverTrigger className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "h-8 w-6 px-0")} title="Variable einfügen">
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-1 max-h-64 overflow-y-auto" align="start">
                        <div className="grid gap-1">
                            <div className="px-2 py-1 text-xs font-semibold text-gray-500">Variable einfügen</div>
                            {AVAILABLE_ATTRIBUTES.map(attr => (
                                <Button 
                                    key={attr.key} 
                                    variant="ghost" 
                                    className="justify-start text-xs h-7 font-normal"
                                    onClick={() => {
                                        // Auto-switch to attribute type if picking a variable
                                        onUpdateCell({ type: 'attribute', value: `{{${attr.key}}}` });
                                    }}
                                >
                                    <span className="font-mono mr-2 text-blue-600 bg-blue-50 px-1 rounded">{'{}'}</span>
                                    {attr.label}
                                </Button>
                            ))}
                        </div>
                    </PopoverContent>
                 </Popover>
             </div>
          )}
        </div>

        {/* Alignment */}
        <div className="flex items-center border-r pr-2 gap-1">
          <Button 
            variant={styles.textAlign === 'left' || !styles.textAlign ? 'secondary' : 'ghost'} 
            size="icon" className="h-8 w-8" 
            onClick={() => onUpdateStyle({ textAlign: 'left' })}
          >
            <AlignLeft className="w-4 h-4" />
          </Button>
          <Button 
            variant={styles.textAlign === 'center' ? 'secondary' : 'ghost'} 
            size="icon" className="h-8 w-8"
            onClick={() => onUpdateStyle({ textAlign: 'center' })}
          >
            <AlignCenter className="w-4 h-4" />
          </Button>
          <Button 
            variant={styles.textAlign === 'right' ? 'secondary' : 'ghost'} 
            size="icon" className="h-8 w-8"
            onClick={() => onUpdateStyle({ textAlign: 'right' })}
          >
            <AlignRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Font & Color */}
        <div className="flex items-center gap-1">
          <Button 
            variant={styles.fontWeight === 'bold' ? 'secondary' : 'ghost'} 
            size="icon" className="h-8 w-8"
            onClick={() => onUpdateStyle({ fontWeight: styles.fontWeight === 'bold' ? 'normal' : 'bold' })}
          >
            <Bold className="w-4 h-4" />
          </Button>
          
          <div className="w-20">
              <Select 
                  value={String(styles.fontSize || 3)} 
                  onValueChange={(v) => onUpdateStyle({ fontSize: parseFloat(v) })}
              >
                  <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Size" />
                  </SelectTrigger>
                  <SelectContent>
                      <SelectItem value="1.5">1.5mm</SelectItem>
                      <SelectItem value="2">2.0mm</SelectItem>
                      <SelectItem value="2.5">2.5mm</SelectItem>
                      <SelectItem value="3">3.0mm</SelectItem>
                      <SelectItem value="3.5">3.5mm</SelectItem>
                      <SelectItem value="4">4.0mm</SelectItem>
                      <SelectItem value="5">5.0mm</SelectItem>
                      <SelectItem value="6">6.0mm</SelectItem>
                      <SelectItem value="8">8.0mm</SelectItem>
                  </SelectContent>
              </Select>
          </div>

          <Popover>
            <PopoverTrigger className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "h-8 w-8 relative")}>
                <Palette className="w-4 h-4" />
                <div className="absolute bottom-1 right-1 w-2 h-2 rounded-full border border-gray-300" 
                     style={{ backgroundColor: styles.backgroundColor || 'transparent' }} />
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3">
               <div className="space-y-2">
                 <h4 className="font-medium text-xs text-gray-500">Hintergrundfarbe</h4>
                 <div className="flex flex-wrap gap-2">
                    {['transparent', '#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db', '#fee2e2', '#fef3c7', '#d1fae5', '#dbeafe', '#e0e7ff', '#fae8ff'].map(color => (
                        <button
                           key={color}
                           className="w-8 h-8 rounded-md border shadow-sm hover:scale-110 transition-transform"
                           style={{ backgroundColor: color }}
                           onClick={() => onUpdateStyle({ backgroundColor: color === 'transparent' ? undefined : color })}
                           title={color}
                        />
                    ))}
                 </div>
                 <div className="pt-2 border-t">
                    <div className="flex items-center gap-2">
                       <span className="text-xs">Hex:</span>
                       <Input 
                          className="h-6 text-xs" 
                          value={styles.backgroundColor || ''}
                          onChange={(e) => onUpdateStyle({ backgroundColor: e.target.value })}
                          placeholder="#ffffff"
                       />
                    </div>
                 </div>
               </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Bottom Row: Borders & Grid Ops */}
      <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-xl border border-gray-200">
         {/* Excel-like Borders Dropdown */}
         <div className="flex items-center border-r pr-2 gap-1">
            <div className="w-16 mr-1">
                 <Select 
                    value={String(borderWidth)} 
                    onValueChange={(v) => setBorderWidth(parseFloat(v))}
                >
                    <SelectTrigger className="h-8 text-xs px-2">
                        <SelectValue placeholder="W" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="0.1">0.1</SelectItem>
                        <SelectItem value="0.18">0.18</SelectItem>
                        <SelectItem value="0.25">0.25</SelectItem>
                        <SelectItem value="0.35">0.35</SelectItem>
                        <SelectItem value="0.5">0.5</SelectItem>
                        <SelectItem value="0.7">0.7</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <Popover>
               <PopoverTrigger className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "h-8 w-8")}>
                  <Grid3X3 className="w-4 h-4" />
               </PopoverTrigger>
               <PopoverContent className="w-56 p-2">
                  <div className="grid grid-cols-4 gap-1">
                     <Button variant="ghost" size="icon" title="Kein Rahmen" onClick={() => applyBorder('no')}>
                        <Ban className="w-4 h-4 text-gray-400" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Alle Rahmen" onClick={() => applyBorder('all')}>
                        <Grid3X3 className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Rahmen Außen" onClick={() => applyBorder('outside')}>
                        <Square className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Fetter Rahmen Außen" onClick={() => applyBorder('thick_outside')}>
                        <Square className="w-4 h-4 stroke-[3px]" />
                     </Button>
                     
                     <div className="col-span-4 h-px bg-gray-100 my-1" />
                     
                     <Button variant="ghost" size="icon" title="Unten" onClick={() => applyBorder('bottom')}>
                        <ArrowDownFromLine className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Oben" onClick={() => applyBorder('top')}>
                        <ArrowUpFromLine className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Links" onClick={() => applyBorder('left')}>
                        <ArrowLeftFromLine className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Rechts" onClick={() => applyBorder('right')}>
                        <ArrowRightFromLine className="w-4 h-4" />
                     </Button>

                     <div className="col-span-4 h-px bg-gray-100 my-1" />

                     <Button variant="ghost" size="icon" title="Innen Horizontal" onClick={() => applyBorder('inside_h')}>
                        <GripHorizontal className="w-4 h-4" />
                     </Button>
                     <Button variant="ghost" size="icon" title="Innen Vertikal" onClick={() => applyBorder('inside_v')}>
                        <GripVertical className="w-4 h-4" />
                     </Button>
                  </div>
               </PopoverContent>
            </Popover>
         </div>

         {/* Grid Tools */}
         <div className="flex items-center gap-1">
            {/* Merge/Split */}
            {isMultiSelect ? (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 bg-blue-50" onClick={() => onGridAction({ type: 'MERGE_SELECTION' })} title="Auswahl verbinden">
                    <Merge className="w-4 h-4" />
                </Button>
            ) : (
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onGridAction({ type: 'MERGE', direction: 'right' })} title="Verbinden (Rechts)">
                    <Merge className="w-4 h-4 rotate-90" />
                </Button>
            )}
            
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onGridAction({ type: 'SPLIT' })} title="Trennen">
                <Split className="w-4 h-4" />
            </Button>

            <div className="w-px h-6 bg-gray-200 mx-1" />

            {/* Rows/Cols */}
            <Popover>
              <PopoverTrigger className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "h-8 w-8 text-blue-600")}>
                 <Columns className="w-4 h-4" />
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1">
                 <Button variant="ghost" className="w-full justify-start text-xs h-8" onClick={() => onGridAction({ type: 'INSERT_COL', after: false })}>
                    <Plus className="w-3 h-3 mr-2" /> Spalte links
                 </Button>
                 <Button variant="ghost" className="w-full justify-start text-xs h-8" onClick={() => onGridAction({ type: 'INSERT_COL', after: true })}>
                    <Plus className="w-3 h-3 mr-2" /> Spalte rechts
                 </Button>
                 <Button variant="ghost" className="w-full justify-start text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onGridAction({ type: 'DELETE_COL' })}>
                    <Trash2 className="w-3 h-3 mr-2" /> Spalte löschen
                 </Button>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger className={cn(buttonVariants({ variant: "ghost", size: "icon" }), "h-8 w-8 text-blue-600")}>
                 <Rows className="w-4 h-4" />
              </PopoverTrigger>
              <PopoverContent className="w-40 p-1">
                 <Button variant="ghost" className="w-full justify-start text-xs h-8" onClick={() => onGridAction({ type: 'INSERT_ROW', after: false })}>
                    <Plus className="w-3 h-3 mr-2" /> Zeile oben
                 </Button>
                 <Button variant="ghost" className="w-full justify-start text-xs h-8" onClick={() => onGridAction({ type: 'INSERT_ROW', after: true })}>
                    <Plus className="w-3 h-3 mr-2" /> Zeile unten
                 </Button>
                 <Button variant="ghost" className="w-full justify-start text-xs h-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onGridAction({ type: 'DELETE_ROW' })}>
                    <Trash2 className="w-3 h-3 mr-2" /> Zeile löschen
                 </Button>
              </PopoverContent>
            </Popover>

            <div className="w-px h-6 bg-gray-200 mx-1" />

            <Button 
               variant="ghost" 
               size="icon" 
               className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" 
               onClick={() => onGridAction({ type: 'DELETE_COL' })} 
               title="Spalte löschen"
            >
               <div className="relative">
                   <Columns className="w-4 h-4" />
                   <X className="w-3 h-3 absolute -bottom-1 -right-1 stroke-[2px] bg-white rounded-full" />
               </div>
            </Button>

            <Button 
               variant="ghost" 
               size="icon" 
               className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50" 
               onClick={() => onGridAction({ type: 'DELETE_ROW' })} 
               title="Zeile löschen"
            >
               <div className="relative">
                   <Rows className="w-4 h-4" />
                   <X className="w-3 h-3 absolute -bottom-1 -right-1 stroke-[2px] bg-white rounded-full" />
               </div>
            </Button>

         </div>
      </div>
    </div>
  );
}
